import { FC } from 'react';

import { Flex, Spin, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useTheme } from 'styled-components';
import useSWR from 'swr';

import Table from '@/components/RTable';
import {
  SimulationResult,
  SimulationResultYearlyFinancialAnalysis,
  SimulationResultYearlyFinancialAnalysisData,
} from '@/types/simulations';
import { formatNumberWithSuffix, getYears } from '@/utils';

import { StyledTable } from './styles';

interface AncillaryTableProps {
  simulationData: SimulationResult;
  simulationId?: number;
}

const AncillaryTable: FC<AncillaryTableProps> = ({
  simulationData,
  simulationId,
}) => {
  const { t } = useTranslation();
  const params = useParams<Record<string, string>>();
  const resultId = simulationId || params.resultId;
  const { data: financialData, isLoading } =
    useSWR<SimulationResultYearlyFinancialAnalysis>(
      `/simulation/${resultId}/yearly_financial_analysis/`
    );

  const theme = useTheme();
  // Ignoring the hour and minute part of the date because of the timezone issue
  const years: string[] = getYears(
    simulationData?.startDate.slice(0, 10) || '',
    simulationData?.endDate.slice(0, 10) || ''
  ).map(String);

  const scrollX =
    years.length > 5 ? 600 + years.length * 50 : 600 + years.length * 50;

  type DataType = {
    key: React.Key;
    name: string;
    width: number;
  };

  const columns: ColumnsType<DataType> = [
    {
      title: '',
      dataIndex: 'name',
      key: 'name',
      width: 300,
      fixed: 'left',
    },
    ...years.map((year) => ({
      title: year,
      dataIndex: year,
      key: year,
      width: 85,
    })),
  ];

  const getValuesForYears = (
    graphData: SimulationResultYearlyFinancialAnalysisData[] | undefined,
    key: string
  ) => {
    const values: Record<string, number> = {};
    if (graphData) {
      years.forEach((year) => {
        const data = graphData.find((item) => item.year === Number(year));
        values[year] = data ? data[key] : 0;
      });
    }
    return values;
  };

  const dataSource = [
    {
      key: '1',
      name: (
        <Flex
          gap={6}
          align="center"
          style={{
            padding: '10px 24px',
            background: theme.colors.blueLightest,
          }}
        >
          <Typography.Text className="fs-14-bold">
            {t('result.analysis.ancillaryTable.offeredCapacityForPfc')}
          </Typography.Text>
        </Flex>
      ),
      ...Object.fromEntries(
        Object.entries(
          getValuesForYears(financialData?.graphData, 'offeredCapacityOfPfc')
        ).map(([year, value]) => [year, formatNumberWithSuffix(value)])
      ),
    },
    {
      key: '2',
      name: (
        <Flex
          gap={6}
          align="center"
          style={{
            padding: '10px 24px',
            background: theme.colors.blueLightest,
          }}
        >
          <Typography.Text className="fs-14-bold">
            {t('result.analysis.ancillaryTable.reservedCapacityForPfc')}
          </Typography.Text>
        </Flex>
      ),
      ...Object.fromEntries(
        Object.entries(
          getValuesForYears(financialData?.graphData, 'reservedCapacityOfPfc')
        ).map(([year, value]) => [year, formatNumberWithSuffix(value)])
      ),
    },
    {
      key: '3',
      name: (
        <Flex
          gap={6}
          align="center"
          style={{
            padding: '10px 24px',
            background: theme.colors.blueLightest,
          }}
        >
          <Typography.Text className="fs-14-bold">
            {t('result.analysis.ancillaryTable.dischargedEnergyForPfc')}
          </Typography.Text>
        </Flex>
      ),
      ...Object.fromEntries(
        Object.entries(
          getValuesForYears(financialData?.graphData, 'dischargedEnergyForPfc')
        ).map(([year, value]) => [year, formatNumberWithSuffix(value)])
      ),
    },
    {
      key: '4',
      name: (
        <Flex
          gap={6}
          align="center"
          style={{
            padding: '10px 24px',
            background: theme.colors.blueLightest,
          }}
        >
          <Typography.Text className="fs-14-bold">
            {t('result.analysis.ancillaryTable.offeredCapacityForSfc')}
          </Typography.Text>
        </Flex>
      ),
      ...Object.fromEntries(
        Object.entries(
          getValuesForYears(financialData?.graphData, 'offeredCapacityOfSfc')
        ).map(([year, value]) => [year, formatNumberWithSuffix(value)])
      ),
    },
    {
      key: '5',
      name: (
        <Flex
          gap={6}
          align="center"
          style={{
            padding: '10px 24px',
            background: theme.colors.blueLightest,
          }}
        >
          <Typography.Text className="fs-14-bold">
            {t('result.analysis.ancillaryTable.reservedCapacityForSfc')}
          </Typography.Text>
        </Flex>
      ),
      ...Object.fromEntries(
        Object.entries(
          getValuesForYears(financialData?.graphData, 'reservedCapacityOfSfc')
        ).map(([year, value]) => [year, formatNumberWithSuffix(value)])
      ),
    },
    {
      key: '6',
      name: (
        <Flex
          gap={6}
          align="center"
          style={{
            padding: '10px 24px',
            background: theme.colors.blueLightest,
          }}
        >
          <Typography.Text className="fs-14-bold">
            {t('result.analysis.ancillaryTable.dischargedEnergyForSfc')}
          </Typography.Text>
        </Flex>
      ),
      ...Object.fromEntries(
        Object.entries(
          getValuesForYears(financialData?.graphData, 'dischargedEnergyForSfc')
        ).map(([year, value]) => [year, formatNumberWithSuffix(value)])
      ),
    },
  ];

  return (
    <StyledTable>
      {isLoading ? (
        <Spin style={{ margin: '20px' }} />
      ) : (
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          bordered
          size="small"
          scroll={{ x: scrollX }}
        />
      )}
    </StyledTable>
  );
};
export default AncillaryTable;
